/**
 * Regex Validate Tool
 *
 * Validate regex patterns using the RegExp constructor.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

const FLAG_DESCRIPTIONS: Record<string, string> = {
  g: 'global — match all occurrences',
  i: 'ignoreCase — case-insensitive matching',
  m: 'multiline — ^ and $ match line boundaries',
  s: 'dotAll — . matches newlines',
  u: 'unicode — enable Unicode features',
  v: 'unicodeSets — extended Unicode sets',
  y: 'sticky — match from lastIndex only',
  d: 'hasIndices — generate index info',
};

export const RegexValidateTool: ITransformerTool = {
  id: 'regex-validate',
  name: 'Regex Validate',
  description: 'Validate regex patterns and show details',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['regex', 'regexp', 'validate', 'check', 'verify', 'pattern'],

  seo: {
    slug: 'regex-validator',
    pageTitle: 'Regex Validator - Free Online Pattern Checker',
    metaDescription: 'Validate regular expression patterns and see flags, groups, and details. Catch syntax errors instantly. Fast, private, runs entirely in your browser.',
    keywords: ['regex validator', 'regex checker', 'validate regex online', 'regexp tester', 'regex syntax checker'],
    relatedTools: ['regex-tester'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT, SEMANTIC_TYPES.REGEX],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'flags',
      type: 'string',
      label: 'Flags',
      defaultValue: '',
      description: 'Regex flags to apply (e.g., gi, ms)',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let pattern = input.trim();
    let flags = String(config.flags ?? '');

    // Support /pattern/flags syntax
    const slashMatch = pattern.match(/^\/(.+)\/([gimsuyv]*)$/s);
    if (slashMatch) {
      pattern = slashMatch[1];
      if (!flags) flags = slashMatch[2];
    }

    if (!pattern) return '✗ Empty pattern';

    try {
      const regex = new RegExp(pattern, flags);

      const parts: string[] = ['✓ Valid regex'];
      parts.push(`Pattern: ${regex.source}`);

      if (regex.flags) {
        parts.push(`Flags: ${regex.flags}`);
        for (const flag of regex.flags) {
          const desc = FLAG_DESCRIPTIONS[flag];
          if (desc) parts.push(`  ${flag}: ${desc}`);
        }
      } else {
        parts.push('Flags: (none)');
      }

      // Detect named capture groups
      const groupPattern = /\(\?<([^>]+)>/g;
      const groups: string[] = [];
      let match;
      while ((match = groupPattern.exec(pattern)) !== null) {
        groups.push(match[1]);
      }

      // Count all capture groups
      const allGroups = pattern.match(/\((?!\?[:<!=!])/g);
      const totalGroups = (allGroups ? allGroups.length : 0) + groups.length;

      if (totalGroups > 0) {
        parts.push(`Capture groups: ${totalGroups}`);
      }
      if (groups.length > 0) {
        parts.push(`Named groups: ${groups.join(', ')}`);
      }

      return parts.join('\n');
    } catch (e) {
      const error = e as Error;
      return `✗ Invalid regex\nError: ${error.message}`;
    }
  },
};
